// Psychologist/PsychologistWorkParamsForm.tsx

import React, {useContext, useEffect, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {axios} from "Auth/axiosConfig";
import {Message} from "Core/components/Message";
import {useErrorProcessing} from "Core/components/ErrorProvider";
import Button from "Core/components/elements/Button/Button";
import {IPsychologist} from "../../Types/psychology/psychologist";
import {AuthContext, AuthContextType} from "Auth/AuthContext";
import {useDispatch, useSelector} from 'react-redux';
import ConsultationConfigsPicker from '../Consultation/ConsultationConfigsPicker';
import {AppDispatch, RootState} from "Redux/store";
import {fetchPsychologyParams} from "Redux/psychologyParamsSlice";
import {Checkbox, FormControlLabel, Typography} from '@mui/material';
import ConsultationCommunicationTypesPicker from "Consultation/ConsultationCommunicationTypesPicker";
import ConsultationLanguagesPicker from "Consultation/ConsultationLanguagesPicker";
import SpecializationsPicker from "Psychologist/SpecializationsPicker";
import SkillsPicker from "Psychologist/SkillsPicker";
import {FC, FRC} from "WideLayout/Layouts";
import Divider from "Core/components/elements/Divider";

const PsychologistWorkParamsForm: React.FC = () => {
    const {byResponse} = useErrorProcessing();
    const {user} = useContext(AuthContext) as AuthContextType;
    const [psychologist, setPsychologist] = useState<IPsychologist | null>(null);

    const {
        handleSubmit,
        control,
        reset,
        formState,
    } = useForm({
        defaultValues: {
            consultation_languages: [] as number[],
            consultation_communication_types: [] as number[],
            has_children: false,
            skills: [] as number[],
            specializations: [] as number[],
            consultation_configs: [] as number[],
        },
    });

    const dispatch = useDispatch<AppDispatch>();
    const {status} = useSelector(
        (state: RootState) => state.psychologyParams
    );

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    useEffect(() => {
        fetchPsychologistDetails();
        if (status === 'idle') dispatch(fetchPsychologyParams());
    }, [dispatch, status]);

    const fetchPsychologistDetails = async () => {
        if (!user || !user.psychologist) return;
        try {
            const response = await axios.get(
                `/api/v1/psychologists/${user.id}/`
            );
            const data: IPsychologist = response.data;
            setPsychologist(data);

            reset({
                consultation_languages: data.consultation_languages
                    ? data.consultation_languages.map((l: any) => l.id)
                    : [],
                consultation_communication_types: data.consultation_communication_types
                    ? data.consultation_communication_types.map((c: any) => c.id)
                    : [],
                has_children: data.has_children,
                skills: data.skills ? data.skills.map((s: any) => s.id) : [],
                specializations: data.specializations
                    ? data.specializations.map((s: any) => s.id)
                    : [],
                consultation_configs: data.consultation_configs
                    ? data.consultation_configs.map((c: any) => c.id)
                    : [],
            });
        } catch (error) {
            byResponse(error);
        }
    };

    const onSubmit = (formData: any) => {
        if (isSubmitting) return;
        setIsSubmitting(true);

        axios.patch('/api/v1/psychologist/update/', formData)
            .then(() =>
                Message.success('Данные успешно обновлены'))
            .catch(e => byResponse(e))
            .finally(() => setIsSubmitting(false));
    };

    if (!psychologist) return null;
    if (status === 'loading') return <div>Загрузка...</div>;
    if (status === 'failed') return <div>Ошибка загрузки данных</div>;

    return (
        <FC component={'form'} g={2} pb={'4.5rem'} onSubmit={handleSubmit(onSubmit)}>
            {/* SkillsPicker */}
            <FC>
                <Typography variant="h6">Навыки</Typography>
                <SkillsPicker control={control}/>
            </FC>
            <Divider/>

            {/* SpecializationsPicker */}
            <FC>
                <Typography variant="h6">Специализации</Typography>
                <SpecializationsPicker control={control}/>
            </FC>
            <Divider/>

            {/* ConsultationLanguagesPicker */}
            <FC>
                <Typography variant="h6">Языки консультации</Typography>
                <ConsultationLanguagesPicker control={control}/>
            </FC>
            <Divider/>

            {/* ConsultationCommunicationTypesPicker */}
            <FC>
                <Typography variant="h6">Способы связи</Typography>
                <ConsultationCommunicationTypesPicker control={control}/>
            </FC>
            <Divider/>

            {/* Чекбокс "Есть дети" */}
            <Controller
                name="has_children"
                control={control}
                render={({field}) => (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={field.value}
                                onChange={(e) => field.onChange(e.target.checked)}
                            />
                        }
                        label="Есть дети"
                    />
                )}/>
            <Divider/>

            {/* ConsultationConfigsPicker */}
            <FC g={2}>
                <Typography variant="h6">Конфигурации</Typography>
                <ConsultationConfigsPicker control={control}/>
            </FC>

            <FRC pos={'absolute'} w={'100%'} bottom={'1rem'} left={0}>
                <Button
                    className={'w-min minw-340px fw-6 fs-5'}
                    type="submit"
                    variant={'contained'}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Сохранение...' : 'Сохранить'}
                </Button>
            </FRC>
        </FC>
    );
};

export default PsychologistWorkParamsForm;
